import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useLocation as useLocationHook } from '../../contexts/LocationContext';
import { useAuth } from '../../contexts/AuthContext';
import AuthButton from '../auth/AuthButton';
import DataDownloadModal from '../auth/DataDownloadModal';
import { NotificationBell } from '../notifications/Notifications';
import './Header.css';
import { HeaderLocationSelector } from './HeaderLocationSelector';

interface HeaderProps {
  onNavigateToOverview: () => void;
  onLocationSelect: (location: string) => void;
  currentLocation?: string;
}

export const Header: React.FC<HeaderProps> = ({ 
  onNavigateToOverview, 
  onLocationSelect,
  currentLocation = ''
}) => {
  const location = useLocation();
  const { selectedGeofips } = useLocationHook();
  const { user } = useAuth();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showDataDownload, setShowDataDownload] = useState(false);

  // Check if we're on the landing page
  const isLandingPage = location.pathname === '/';

  // Handle scroll detection
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogoClick = () => {
    onNavigateToOverview();
  };

  const toggleHamburgerMenu = () => {
    setShowHamburgerMenu(!showHamburgerMenu);
  };

  // Close hamburger menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showHamburgerMenu) {
        const target = event.target as Element;
        if (!target.closest('.hamburger-menu')) {
          setShowHamburgerMenu(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showHamburgerMenu]);

  return (
    <div className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <div className="header-content">
        {/* Desktop Layout */}
        <div className="header-desktop">
          <div className="header-title-section">
            <img 
              src="/data/coolclimate-logo.png" 
              alt="CoolClimate Logo" 
              className="header-logo"
              onClick={handleLogoClick}
            />
            <div className="header-text">
              <h1 onClick={handleLogoClick}>ClimatePlans.org <span style={{ color: 'red', fontSize: '1.2rem', fontWeight: 'bold' }}>Beta</span></h1>
              <p className="header-description">Climate Action Planning Data and Resources for California cities and counties</p>
            </div>
          </div>
          
          <div className="header-actions">
            {/* Location selector - show on all pages except landing page */}
            {!isLandingPage && (
              <div className="header-location-section">
                {!isScrolled ? (
                  <HeaderLocationSelector
                    onLocationSelect={onLocationSelect}
                    currentLocation={currentLocation}
                  />
                ) : (
                  <div className="hamburger-menu">
                    <button 
                      className={`hamburger-button ${showHamburgerMenu ? 'active' : ''}`}
                      onClick={toggleHamburgerMenu}
                      aria-label={`Change location from ${currentLocation || 'current location'}`}
                      title={`Current: ${currentLocation || 'No location selected'}\nClick to change`}
                    >
                      <span className="hamburger-icon">📍</span>
                      {currentLocation && (
                        <span className="current-location-indicator">
                          {currentLocation.length > 12 ? currentLocation.substring(0, 12) + '...' : currentLocation}
                        </span>
                      )}
                    </button>
                    {showHamburgerMenu && (
                      <div className="hamburger-dropdown">
                        <div className="hamburger-location-selector">
                          <HeaderLocationSelector
                            onLocationSelect={onLocationSelect}
                            currentLocation={currentLocation}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Data download button - show only when logged in and location is selected */}
            {user && !isLandingPage && currentLocation && selectedGeofips && (
              <div className="header-download-section">
                <button
                  className="header-download-button"
                  onClick={() => setShowDataDownload(true)}
                  title="Download all data for this location"
                >
                  📥 Download Data
                </button>
              </div>
            )}
            
            {/* Notifications */}
            <div className="header-notifications-section">
              <NotificationBell />
            </div>
            
            {/* Authentication button */}
            <div className="header-auth-section">
              <AuthButton />
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="header-mobile">
          {/* Left: ClimatePlans title */}
          <div className="header-title-section">
            <h1 onClick={handleLogoClick}>ClimatePlans <span style={{ color: 'red', fontSize: '0.9rem', fontWeight: 'bold' }}>Beta</span></h1>
          </div>
          
          {/* Center: Location selector - show on all pages except landing page */}
          {!isLandingPage && (
            <div className="header-location-section">
              <HeaderLocationSelector
                onLocationSelect={onLocationSelect}
                currentLocation={currentLocation}
              />
            </div>
          )}
          
          {/* Right: Menu button */}
          <div className="header-menu-section">
            <div className="hamburger-menu">
              <button 
                className={`hamburger-button ${showHamburgerMenu ? 'active' : ''}`}
                onClick={toggleHamburgerMenu}
                aria-label="Open menu"
              >
                <div className="hamburger-icon">
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                  <span className="hamburger-line"></span>
                </div>
              </button>
              {showHamburgerMenu && (
                <div className="hamburger-dropdown">
                  {/* Data download - only show when logged in and location is selected */}
                  {user && currentLocation && selectedGeofips && (
                    <div className="hamburger-download-section">
                      <button
                        className="hamburger-download-button"
                        onClick={() => {
                          setShowDataDownload(true);
                          setShowHamburgerMenu(false);
                        }}
                      >
                        📥 Download Data
                      </button>
                    </div>
                  )}
                  
                  {/* Authentication section */}
                  <div className="hamburger-auth-section">
                    <AuthButton />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Data Download Modal */}
      <DataDownloadModal
        isOpen={showDataDownload}
        onClose={() => setShowDataDownload(false)}
        location={currentLocation}
        geofips={selectedGeofips}
      />
    </div>
  );
};
